import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  styled,
  alpha,
  useTheme,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  Avatar,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WorkIcon from "@mui/icons-material/Work";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import InfoIcon from "@mui/icons-material/Info";
import EventNoteIcon from "@mui/icons-material/EventNote";
import HistoryIcon from "@mui/icons-material/History";
import NotificationSnackbar from "./NotificationBar";

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": { height: "8px", width: "8px" },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.4) },
  },
}));

function EmployeeExitHistory() {
  const theme = useTheme();
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";
  const DEFAULT_COLOR = "#FFFFFF";

  // Search & List States
  const [searchTerm, setSearchTerm] = useState("");
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showData, setShowData] = useState(false);

  // Dropdown States
  const [exitTypes, setExitTypes] = useState([]);

  // Modal & Menu States
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Edit Form State
  const [editFormData, setEditFormData] = useState({
    id: "",
    effectiveDate: "",
    exitType: "",
    ToBePaidTerminal: false,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  // Snackbar State
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Initial Fetch for Exit Types
  useEffect(() => {
    const fetchExitTypes = async () => {
      try {
        const res = await fetch("/api/support/exit-types");
        const data = await res.json();
        setExitTypes(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load exit types", error);
      }
    };
    fetchExitTypes();
  }, []);

  // Format Helpers
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toYYYYMMDD = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  // Search Logic
  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setShowData(false);
      setHistoryList([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(
        "/api/support/employee-exit-history-by-search-term",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ searchTerm: term }),
        }
      );
      const json = await res.json().catch(() => null);

      if (!res.ok) {
        const msg =
          typeof json === "string"
            ? json
            : (json && (json.message || json.Message)) || `HTTP ${res.status}`;
        setError(msg);
        setShowData(true);
        setHistoryList([]);
        return;
      }
      
      let list = [];
      if (Array.isArray(json)) {
        list = json;
      } else if (typeof json === "object" && json !== null) {
        list = Array.isArray(json.data) ? json.data : Object.values(json);
      }

      if (list.length > 0) {
        setHistoryList(list);
        setShowData(true);
        setError(null);
      } else {
        setHistoryList([]);
        setShowData(true);
        setError("No exit history records found for this search.");
      }
    } catch (err) {
      console.error("Search failed:", err);
      setError("Search failed. Please try again.");
      setShowData(true);
      setHistoryList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setError(null);
    setShowData(false);
    setHistoryList([]);
  };

  // Menu Handlers
  const handleMenuClick = (event, record) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenView = () => {
    setViewModalOpen(true);
    handleMenuClose();
  };

  const handleOpenEdit = () => {
    // Attempt to map the table string (e.g. "Internal Transfer") to the ID fetched from the API
    const matchedExitType = exitTypes.find(
      (et) => et.name.toLowerCase() === selectedRecord.ExitType?.toLowerCase()
    );

    setEditFormData({
      id: selectedRecord.id || selectedRecord.employeeId, // Use 'id' if available, fallback to employeeId
      effectiveDate: toYYYYMMDD(selectedRecord.EffectiveDate),
      exitType: matchedExitType ? matchedExitType.id : "",
      ToBePaidTerminal: selectedRecord.ToBePaidTerminal ?? false, // Init based on DB value
    });

    setEditModalOpen(true);
    handleMenuClose();
  };

  // Update Submission Logic
  const handleEditSubmit = async () => {
    try {
      setUpdating(true);
      const res = await fetch("/api/support/exit-request", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: editFormData.id,
          effectiveDate: editFormData.effectiveDate,
          exitType: editFormData.exitType,
          ToBePaidTerminal: editFormData.ToBePaidTerminal,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        throw new Error(errorData?.message || "Failed to update record.");
      }

      setSnackbarMessage("Record updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setEditModalOpen(false);

      // Refresh the table data silently
      handleSearch(); 
    } catch (err) {
      console.error("Update error:", err);
      setSnackbarMessage(err.message || "An error occurred while updating.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setUpdating(false);
    }
  };

  // Small Detail Component for View Modal
  const DetailItem = ({ label, value, textColor = PRIMARY_COLOR }) => (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 600,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: textColor, fontWeight: 500, mt: 0.5 }}
      >
        {value !== null && value !== undefined && value !== ""
          ? String(value)
          : "-"}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        minHeight: "75vh",
      }}
    >
      {/* HEADER */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY_COLOR }}>
          Employee Exit History
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Search and view employee exit, transfer, and transition records.
        </Typography>
      </Box>

      {/* SEARCH AREA */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
          bgcolor: alpha(PRIMARY_COLOR, 0.015),
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 3,
          }}
        >
          <TextField
            placeholder="Search by Employee Name, ID..."
            size="small"
            value={searchTerm}
            onChange={(e) => {
              const v = e.target.value;
              setSearchTerm(v);
              if (!v.trim()) setShowData(false);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm && (
                    <IconButton
                      aria-label="clear"
                      size="small"
                      onClick={handleClearSearch}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              maxWidth: 500,
              bgcolor: "white",
              borderRadius: 1,
            }}
          />
          <Button
            variant="contained"
            disableElevation
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SearchIcon />
              )
            }
            onClick={handleSearch}
            disabled={loading}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              bgcolor: PRIMARY_COLOR,
              "&:hover": { bgcolor: SECONDARY_COLOR },
            }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </Box>

        <Collapse in={showData} timeout="auto">
          {error ? (
            <Typography
              color="error"
              sx={{ fontSize: "0.95rem", fontWeight: 500 }}
            >
              {error}
            </Typography>
          ) : historyList && historyList.length > 0 ? (
            <Box>
              <Typography
                sx={{ fontWeight: 600, color: PRIMARY_COLOR, mb: 1.5 }}
              >
                Found {historyList.length} Result
                {historyList.length > 1 ? "s" : ""}
              </Typography>
              <ResponsiveTableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Emp. ID
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Employee Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        From Position
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        To Position
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Exit Type
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Effective Date
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyList.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.employeeId ?? "-"}
                        </TableCell>
                        <TableCell>{row.employee_name || "-"}</TableCell>
                        <TableCell>{row.FROM_Position || "-"}</TableCell>
                        <TableCell>{row.To_Position || "-"}</TableCell>
                        <TableCell>{row.ExitType || "-"}</TableCell>
                        <TableCell>{formatDate(row.EffectiveDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.Exit_Status || "Pending"}
                            size="small"
                            sx={{
                              backgroundColor:
                                row.Exit_Status === "Approved"
                                  ? alpha(theme.palette.success.main, 0.15)
                                  : alpha(theme.palette.warning.main, 0.15),
                              color:
                                row.Exit_Status === "Approved"
                                  ? theme.palette.success.dark
                                  : theme.palette.warning.dark,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, row)}
                            sx={{
                              color: "text.secondary",
                              "&:hover": { color: PRIMARY_COLOR },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTableContainer>
            </Box>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>
              No results found.
            </Typography>
          )}
        </Collapse>
      </Paper>

      {/* SHARED ACTION MENU FOR TABLE */}
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 140, borderRadius: 2, mt: 0.5 },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleOpenEdit} sx={{ fontSize: "0.9rem", py: 1 }}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
          </ListItemIcon>
          Edit Request
        </MenuItem>
        <MenuItem onClick={handleOpenView} sx={{ fontSize: "0.9rem", py: 1 }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" sx={{ color: PRIMARY_COLOR }} />
          </ListItemIcon>
          View Details
        </MenuItem>
      </Menu>

      {/* ========================================================= */}
      {/* MODAL 1: VIEW RECORD DETAILS                              */}
      {/* ========================================================= */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": { borderRadius: "16px" },
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: DEFAULT_COLOR }}
          >
            Exit / Transition Record
          </Typography>
          <IconButton
            onClick={() => setViewModalOpen(false)}
            size="small"
            sx={{ color: DEFAULT_COLOR }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ p: 0, bgcolor: alpha(PRIMARY_COLOR, 0.01) }}
        >
          {selectedRecord && (
            <Box>
              {/* Top Banner Details */}
              <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  bgcolor: "white",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: alpha(PRIMARY_COLOR, 0.1),
                    color: PRIMARY_COLOR,
                    fontWeight: 700,
                  }}
                >
                  {selectedRecord.employee_name
                    ? selectedRecord.employee_name.charAt(0).toUpperCase()
                    : "-"}
                </Avatar>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: PRIMARY_COLOR,
                      lineHeight: 1.2,
                    }}
                  >
                    {selectedRecord.employee_name || "Unknown Employee"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 500, mt: 0.5 }}
                  >
                    Employee ID: {selectedRecord.employeeId || "-"}
                  </Typography>
                </Box>
                <Box sx={{ ml: "auto", textAlign: "right" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                      mb: 0.5,
                      fontWeight: 600,
                    }}
                  >
                    Status
                  </Typography>
                  <Chip
                    label={selectedRecord.Exit_Status || "Pending"}
                    size="small"
                    sx={{
                      backgroundColor:
                        selectedRecord.Exit_Status === "Approved"
                          ? alpha(theme.palette.success.main, 0.15)
                          : alpha(theme.palette.warning.main, 0.15),
                      color:
                        selectedRecord.Exit_Status === "Approved"
                          ? theme.palette.success.dark
                          : theme.palette.warning.dark,
                      fontWeight: 700,
                      px: 1,
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                        height: "100%",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, color: PRIMARY_COLOR }}>
                        <WorkIcon fontSize="small" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "uppercase" }}>Position Details</Typography>
                      </Box>
                      <DetailItem label="Origin Position" value={selectedRecord.FROM_Position} />
                      <Box sx={{ my: 1, color: alpha(PRIMARY_COLOR, 0.4) }}>
                        <ArrowRightAltIcon />
                      </Box>
                      <DetailItem label="Destination Position" value={selectedRecord.To_Position} />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                        height: "100%",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, color: PRIMARY_COLOR }}>
                        <InfoIcon fontSize="small" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "uppercase" }}>Clearance & Transition</Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <DetailItem label="Exit Type" value={selectedRecord.ExitType} />
                        </Grid>
                        <Grid item xs={6}>
                          <DetailItem label="Terminal Paid?" value={selectedRecord.ToBePaidTerminal ? "Yes" : "No"} />
                        </Grid>
                      </Grid>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, color: "text.secondary" }}>
                        <EventNoteIcon fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Effective Date:</Typography>
                        <Typography variant="body2" sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}>
                          {formatDate(selectedRecord.EffectiveDate)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: 3,
                      }}
                    >
                      <DetailItem label="Provided Reason / Comments" value={selectedRecord.reason} textColor="text.primary" />
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{ p: 2, bgcolor: "#f9fafb", borderRadius: 2, display: "flex", flexWrap: "wrap", gap: 3 }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <HistoryIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>Audit Logs</Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", width: "100%" }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>Requested By</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedRecord.RequestedBy || "-"}</Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>{formatDateTime(selectedRecord.createdOn)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>Status Updated By</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedRecord.StatusBy || "-"}</Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>{formatDateTime(selectedRecord.statusOn)}</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3 }}>
          <Button onClick={() => setViewModalOpen(false)} variant="contained" sx={{ bgcolor: PRIMARY_COLOR, px: 3 }}>
            Close Window
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================= */}
      {/* MODAL 2: EDIT RECORD                                      */}
      {/* ========================================================= */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": { borderRadius: "16px" },
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: DEFAULT_COLOR }}
          >
            Edit Exit Record
          </Typography>
          <IconButton
            onClick={() => setEditModalOpen(false)}
            size="small"
            sx={{ color: DEFAULT_COLOR }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedRecord && (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Editing record for{" "}
                  <strong>{selectedRecord.employee_name}</strong> (
                  {selectedRecord.employeeId})
                </Typography>
              </Grid>

              {/* Edit Effective Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Effective Date"
                  InputLabelProps={{ shrink: true }}
                  value={editFormData.effectiveDate}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, effectiveDate: e.target.value })
                  }
                />
              </Grid>

              {/* Edit Exit Type */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  options={exitTypes}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    exitTypes.find((type) => type.id === editFormData.exitType) || null
                  }
                  onChange={(event, newValue) => {
                    setEditFormData((prev) => ({
                      ...prev,
                      exitType: newValue ? newValue.id : "",
                    }));
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ py: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                        >
                          {option.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {option.canBeRehired ? "Can be Rehired" : "Cannot be Rehired"} •{" "}
                          {option.isAttachmentRequired ? "Attachment Req." : "No Attachment"}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Exit Type"
                      placeholder="Search Exit Type..."
                    />
                  )}
                />
              </Grid>

              {/* Edit To Be Paid Terminal */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editFormData.ToBePaidTerminal}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, ToBePaidTerminal: e.target.checked })
                      }
                      color="primary"
                    />
                  }
                  label="To Be Paid Terminal"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3, justifyContent: "space-between" }}>
          <Button
            onClick={() => setEditModalOpen(false)}
            color="inherit"
            disabled={updating}
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: SECONDARY_COLOR, px: 3 }}
            onClick={handleEditSubmit}
            disabled={updating || !editFormData.effectiveDate || editFormData.exitType === ""}
          >
            {updating ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default EmployeeExitHistory;